import { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection, query, where, onSnapshot,
  addDoc, updateDoc, deleteDoc, doc, writeBatch,
  serverTimestamp, Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { getKoreanToday } from '../utils/dateUtils';
import { RECORD_TYPES } from '../utils/recordUtils';
import { scalePageNumber } from '../utils/rescaleUtils';

const RECORDS = 'records';

/**
 * 레거시 메모 id 는 Date.now() 로 만들어졌다.
 * 그럴듯한 epoch ms 면 그대로 작성 시각으로 쓰고, 아니면 그 날 정오로 둔다.
 */
const legacyCreatedAt = (memo, date) => {
  if (typeof memo.id === 'number' && memo.id > 1_000_000_000_000 && memo.id < 4_000_000_000_000) {
    return Timestamp.fromMillis(memo.id);
  }
  const fallback = new Date(`${date}T12:00:00+09:00`);
  return Number.isNaN(fallback.getTime()) ? Timestamp.now() : Timestamp.fromDate(fallback);
};

/**
 * 기존 book.memos 를 records 컬렉션으로 옮긴다.
 *
 * - 원본 book.memos 는 지우지 않는다 (롤백 경로 보존)
 * - book.recordsMigratedAt 플래그로 한 번만 실행
 * - legacyMemoId 로 중복 생성을 막아 재실행해도 안전하다
 *
 * 주의: 레거시 memo.pages 는 '그 날 읽은 쪽수'이지 책의 페이지 번호가 아니다.
 * 의미가 다르므로 page 가 아니라 legacyPages 로 보존한다.
 */
const migrateBookMemos = async (book, existingLegacyIds) => {
  const payloads = [];

  Object.entries(book.memos || {}).forEach(([date, memos]) => {
    (memos || []).forEach(memo => {
      if (!memo?.text) return;
      if (existingLegacyIds.has(`${book.id}:${memo.id}`)) return;

      const payload = {
        userId: book.userId,
        bookId: book.id,
        type: RECORD_TYPES.THOUGHT,
        text: memo.text,
        page: null,
        legacyPages: typeof memo.pages === 'number' ? memo.pages : null,
        parentId: null,
        date,
        order: typeof memo.order === 'number' ? memo.order : 0,
        createdAt: legacyCreatedAt(memo, date),
        updatedAt: legacyCreatedAt(memo, date),
        legacyMemoId: memo.id ?? null
      };
      if (memo.chapter) payload.chapter = memo.chapter;
      if (memo.subChapter) payload.subChapter = memo.subChapter;

      payloads.push(payload);
    });
  });

  // Firestore 배치는 500 작업 제한. 기록을 먼저 다 쓴 뒤 마지막에 플래그를 세운다.
  // 중간에 실패하면 플래그가 없으니 다음 실행에서 legacyMemoId 로 중복을 걸러내고 이어서 쓴다.
  for (let i = 0; i < payloads.length; i += 450) {
    const batch = writeBatch(db);
    payloads.slice(i, i + 450).forEach(payload => {
      batch.set(doc(collection(db, RECORDS)), payload);
    });
    await batch.commit();
  }

  await updateDoc(doc(db, 'books', book.id), { recordsMigratedAt: serverTimestamp() });
  return payloads.length;
};

export const useRecords = (userId, books) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const migratingRef = useRef(new Set());
  const recordsRef = useRef([]);

  useEffect(() => {
    if (!userId) {
      setRecords([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    // orderBy 없이 userId 로만 필터한다. 복합 인덱스가 필요 없고,
    // 정렬은 화면마다 기준이 달라 클라이언트에서 처리한다.
    const q = query(collection(db, RECORDS), where('userId', '==', userId));

    const unsubscribe = onSnapshot(
      q,
      snapshot => {
        const next = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        recordsRef.current = next;
        setRecords(next);
        setLoading(false);
      },
      error => {
        console.error('Failed to subscribe records:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // 레거시 메모 마이그레이션
  useEffect(() => {
    if (!userId || loading || !books?.length) return;

    const pending = books.filter(book =>
      !book.recordsMigratedAt &&
      book.memos &&
      Object.keys(book.memos).length > 0 &&
      !migratingRef.current.has(book.id)
    );
    if (pending.length === 0) return;

    const existingLegacyIds = new Set(
      recordsRef.current
        .filter(r => r.legacyMemoId != null)
        .map(r => `${r.bookId}:${r.legacyMemoId}`)
    );

    pending.forEach(async book => {
      migratingRef.current.add(book.id);
      try {
        const count = await migrateBookMemos(book, existingLegacyIds);
        console.log(`[migration] ${book.title}: 메모 ${count}건을 기록으로 옮겼습니다`);
      } catch (error) {
        // 실패해도 migratingRef 에서 빼지 않는다.
        // books 스냅샷은 자주 갱신되므로, 되돌리면 실패가 반복되며 무한 재시도가 된다.
        // 새로고침하면 다시 시도한다.
        console.error(`[migration] ${book.title} 실패:`, error);
      }
    });
  }, [userId, loading, books]);

  const addRecord = useCallback(async ({
    bookId, type, text, page = null, parentId = null, chapter, subChapter
  }) => {
    if (!userId) throw new Error('로그인이 필요합니다.');
    if (!text?.trim()) throw new Error('내용을 입력해주세요.');

    const payload = {
      userId,
      bookId,
      type: type === RECORD_TYPES.QUOTE ? RECORD_TYPES.QUOTE : RECORD_TYPES.THOUGHT,
      text: text.trim(),
      page: Number.isFinite(page) && page > 0 ? page : null,
      parentId: parentId || null,
      date: getKoreanToday(),
      order: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      legacyMemoId: null
    };
    if (chapter) payload.chapter = chapter;
    if (subChapter) payload.subChapter = subChapter;

    const ref = await addDoc(collection(db, RECORDS), payload);
    return ref.id;
  }, [userId]);

  const updateRecord = useCallback(async (recordId, changes) => {
    const payload = { ...changes, updatedAt: serverTimestamp() };
    if ('text' in payload) payload.text = payload.text.trim();
    if ('page' in payload) {
      payload.page = Number.isFinite(payload.page) && payload.page > 0 ? payload.page : null;
    }
    await updateDoc(doc(db, RECORDS, recordId), payload);
  }, []);

  /** 기록 삭제. 인용을 지우면 거기 달린 생각도 함께 지운다. */
  const deleteRecord = useCallback(async (recordId) => {
    const children = recordsRef.current.filter(r => r.parentId === recordId);
    if (children.length === 0) {
      await deleteDoc(doc(db, RECORDS, recordId));
      return;
    }
    const batch = writeBatch(db);
    batch.delete(doc(db, RECORDS, recordId));
    children.forEach(child => batch.delete(doc(db, RECORDS, child.id)));
    await batch.commit();
  }, []);

  /**
   * 전체 페이지 척도가 바뀌면 기록에 적힌 페이지 번호도 같이 옮긴다.
   * (page 가 없는 기록과 legacyPages 는 건드리지 않는다)
   */
  const rescaleRecordPages = useCallback(async (bookId, ratio, newTotalPages) => {
    if (!Number.isFinite(ratio) || ratio === 1) return 0;

    const targets = recordsRef.current.filter(
      r => r.bookId === bookId && typeof r.page === 'number' && r.page > 0
    );
    if (targets.length === 0) return 0;

    for (let i = 0; i < targets.length; i += 450) {
      const batch = writeBatch(db);
      targets.slice(i, i + 450).forEach(record => {
        batch.update(doc(db, RECORDS, record.id), {
          page: scalePageNumber(record.page, ratio, newTotalPages)
        });
      });
      await batch.commit();
    }
    return targets.length;
  }, []);

  /** 책이 삭제될 때 그 책의 기록도 정리한다. */
  const deleteRecordsForBook = useCallback(async (bookId) => {
    const targets = recordsRef.current.filter(r => r.bookId === bookId);
    if (targets.length === 0) return;

    // Firestore 배치는 500 건 제한
    for (let i = 0; i < targets.length; i += 450) {
      const batch = writeBatch(db);
      targets.slice(i, i + 450).forEach(r => batch.delete(doc(db, RECORDS, r.id)));
      await batch.commit();
    }
  }, []);

  return {
    records,
    recordsLoading: loading,
    addRecord,
    updateRecord,
    deleteRecord,
    deleteRecordsForBook,
    rescaleRecordPages
  };
};
